import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ArdiChannel, RoutineInput, RoutineSchedule } from '../../../common/hooks/use-ardi-api.hook';
import { CHANNEL_ICONS, deliverable } from '../channels/channel-presentation';
import { browserTimezone, pad, scheduleSentence, timezones, weekdayName } from './routine-format';

export interface RoutineDraft {
  title: string;
  prompt: string;
  template: string | null;
  schedule: RoutineSchedule;
  deliverTo: string[];
  enabled: boolean;
}

export const emptyDraft = (): RoutineDraft => ({
  title: '',
  prompt: '',
  template: null,
  schedule: { every: 'weekday', hour: 8, minute: 0, timezone: browserTimezone() },
  deliverTo: [],
  enabled: true,
});

/**
 * One routine: what Ardi is asked, when, and where the answer goes. The
 * sentence under the schedule says it back in words before it is saved.
 */
export function RoutineEditor({
  initial,
  channels,
  onSave,
  onCancel,
  saving,
}: {
  initial: RoutineDraft;
  channels: ArdiChannel[];
  onSave: (draft: RoutineInput) => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<RoutineDraft>(initial);
  useEffect(() => setDraft(initial), [initial]);

  const set = (patch: Partial<RoutineDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const setSchedule = (patch: Partial<RoutineSchedule>) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, ...patch } }));
  const lang = (i18n.language || 'en').slice(0, 2);
  const usable = channels.filter(deliverable);
  const time = `${pad(draft.schedule.hour)}:${pad(draft.schedule.minute)}`;
  const valid = draft.title.trim().length > 0 && draft.prompt.trim().length >= 8;

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onSave(draft);
      }}
      data-testid="routine-editor"
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="routine-title" className="text-xs font-medium text-muted-foreground">{t('ARDI.ROUTINES.FIELD.TITLE')}</Label>
        <Input id="routine-title" value={draft.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} placeholder={t('ARDI.ROUTINES.FIELD.TITLE_PLACEHOLDER') as string} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="routine-prompt" className="text-xs font-medium text-muted-foreground">{t('ARDI.ROUTINES.FIELD.PROMPT')}</Label>
        <Textarea id="routine-prompt" rows={5} value={draft.prompt} maxLength={2000} onChange={(e) => set({ prompt: e.target.value })} placeholder={t('ARDI.ROUTINES.FIELD.PROMPT_PLACEHOLDER') as string} />
        <p className="text-xs text-muted-foreground">{t('ARDI.ROUTINES.FIELD.PROMPT_HINT')}</p>
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-xs font-medium text-muted-foreground">{t('ARDI.ROUTINES.FIELD.WHEN')}</span>
        <ToggleGroup
          type="single"
          variant="outline"
          value={draft.schedule.every}
          onValueChange={(v) => v && setSchedule({ every: v as RoutineSchedule['every'], ...(v === 'week' && draft.schedule.weekday == null ? { weekday: 1 } : {}) })}
          className="self-start"
        >
          <ToggleGroupItem value="day" className="px-3 text-xs">{t('ARDI.ROUTINES.EVERY.DAY')}</ToggleGroupItem>
          <ToggleGroupItem value="weekday" className="px-3 text-xs">{t('ARDI.ROUTINES.EVERY.WEEKDAY')}</ToggleGroupItem>
          <ToggleGroupItem value="week" className="px-3 text-xs">{t('ARDI.ROUTINES.EVERY.WEEK')}</ToggleGroupItem>
        </ToggleGroup>
        <div className="flex flex-wrap items-end gap-3">
          {draft.schedule.every === 'week' && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="routine-weekday" className="text-xs text-muted-foreground">{t('ARDI.ROUTINES.FIELD.DAY')}</Label>
              <NativeSelect id="routine-weekday" value={String(draft.schedule.weekday ?? 1)} onChange={(e) => setSchedule({ weekday: Number(e.target.value) })}>
                {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                  <NativeSelectOption key={d} value={String(d)}>{weekdayName(d, lang)}</NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="routine-time" className="text-xs text-muted-foreground">{t('ARDI.ROUTINES.FIELD.TIME')}</Label>
            <Input
              id="routine-time"
              type="time"
              value={time}
              onChange={(e) => {
                const [h, m] = e.target.value.split(':').map(Number);
                if (!Number.isNaN(h) && !Number.isNaN(m)) setSchedule({ hour: h, minute: m });
              }}
              className="w-32"
            />
          </div>
          <div className="flex min-w-48 flex-1 flex-col gap-1.5">
            <Label htmlFor="routine-tz" className="text-xs text-muted-foreground">{t('ARDI.ROUTINES.FIELD.TIMEZONE')}</Label>
            <NativeSelect id="routine-tz" value={draft.schedule.timezone} onChange={(e) => setSchedule({ timezone: e.target.value })}>
              {timezones(draft.schedule.timezone).map((z) => (
                <NativeSelectOption key={z} value={z}>{z.replace(/_/g, ' ')}</NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
        </div>
        <p className="text-sm" data-testid="routine-sentence">{scheduleSentence(t as any, draft.schedule, lang)}</p>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t('ARDI.ROUTINES.FIELD.DELIVER')}</span>
        <label className="flex items-center gap-2.5 rounded-lg border p-2.5 text-sm opacity-80">
          <Checkbox checked disabled />
          {t('ARDI.ROUTINES.DELIVER_HOME')}
        </label>
        {usable.map((c) => {
          const Icon = CHANNEL_ICONS[c.kind];
          const id = `deliver-${c.uuid}`;
          const on = draft.deliverTo.includes(c.uuid);
          return (
            <label key={c.uuid} htmlFor={id} className="flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm hover:bg-accent">
              <Checkbox id={id} checked={on} onCheckedChange={(v) => set({ deliverTo: v === true ? [...draft.deliverTo, c.uuid] : draft.deliverTo.filter((x) => x !== c.uuid) })} />
              <Icon className="size-4 text-muted-foreground" aria-hidden />
              {c.name}
            </label>
          );
        })}
        {!usable.length && <p className="text-xs text-muted-foreground">{t('ARDI.ROUTINES.DELIVER_NO_CHANNELS')}</p>}
      </div>

      <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
        <div>
          <Label htmlFor="routine-enabled" className="text-sm font-normal">{t('ARDI.ROUTINES.FIELD.ENABLED')}</Label>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t('ARDI.ROUTINES.CREDITS_NOTE')}</p>
        </div>
        <Switch id="routine-enabled" checked={draft.enabled} onCheckedChange={(v) => set({ enabled: v })} />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>{t('ARDI.COMMON.CANCEL')}</Button>
        <Button type="submit" disabled={!valid || saving}>
          {saving && <Loader2 className="animate-spin" />}
          {t('ARDI.ROUTINES.SAVE')}
        </Button>
      </div>
    </form>
  );
}
